// Popover "Aparência": Claro / Escuro / Sistema e cor de destaque (5 presets
// + cor livre). Persiste em localStorage (preferredTheme, primaryColor*).
import React, { useContext, useState, useRef } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import AddIcon from "@material-ui/icons/Add";
import ColorModeContext from "../themeContext";
import { AEROS, stateText } from "../../styles/aerosTokens";
import useHeaderStyles from "./headerStyles";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    grid: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 },
    opt: { border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, borderRadius: 10, padding: 8, display: "flex", flexDirection: "column", gap: 6, alignItems: "center", background: "none", cursor: "pointer", font: "inherit", color: theme.palette.text.secondary, fontSize: 11, fontWeight: 600 },
    optOn: { borderColor: "rgba(160,68,255,0.5)", backgroundColor: alpha("#A044FF", 0.08), color: stateText(theme, "#C08BFF", "purple"), fontWeight: 700 },
    prev: { width: "100%", height: 38, borderRadius: 6, overflow: "hidden", border: "1px solid rgba(128,128,128,0.3)" },
    bar: { height: 8 },
    swatches: { display: "flex", gap: 8 },
    sw: { width: 26, height: 26, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0, flex: "none" },
    swOn: { boxShadow: `0 0 0 2px ${theme.palette.aeros.surface2 || theme.palette.background.paper}, 0 0 0 4px ${AEROS.cyan}` },
    add: { width: 26, height: 26, borderRadius: "50%", border: "1px dashed rgba(128,128,128,0.5)", background: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: theme.palette.text.secondary, padding: 0, "& .MuiSvgIcon-root": { fontSize: 14 } },
    sep: { borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`, paddingTop: 12 },
  };
});

export const ACCENTS = ["#A044FF", "#9d35f0", "#22D3EE", "#34D399", "#FBBF24"];

// Conteúdo do popover, reutilizado no modal de Preferências.
export function AppearancePanel() {
  const h = useHeaderStyles();
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  const colorRef = useRef(null);
  const pref = colorMode.preference || colorMode.mode;
  const accent = localStorage.getItem("primaryColorDark") || localStorage.getItem("primaryColorLight") || AEROS.defaultPrimary;

  const setAccent = (hex) => {
    localStorage.setItem("primaryColorLight", hex);
    localStorage.setItem("primaryColorDark", hex);
    colorMode.setPrimaryColorLight(hex);
    colorMode.setPrimaryColorDark(hex);
  };

  return (
    <>
      <div className={classes.grid}>
        {[["light", i18n.t("aeros.header.light")], ["dark", i18n.t("aeros.header.dark")], ["system", i18n.t("aeros.header.system")]].map(([k, label]) => (
          <button key={k} type="button" className={`${classes.opt} ${pref === k ? classes.optOn : ""}`} onClick={() => colorMode.setColorMode(k)} aria-pressed={pref === k}>
            <div className={classes.prev} style={k === "light" ? { background: "#F5F6FA" } : k === "dark" ? { background: "#0B0D14" } : { background: "linear-gradient(90deg,#F5F6FA 50%,#0B0D14 50%)" }}>
              {k !== "system" && <div className={classes.bar} style={{ background: k === "light" ? accent : "linear-gradient(90deg,#A044FF,#22D3EE)" }} />}
            </div>
            {label}
          </button>
        ))}
      </div>
      <div className={classes.sep}>
        <div className={h.sectionLabel}>{i18n.t("aeros.header.accent")}</div>
        <div className={classes.swatches}>
          {ACCENTS.map((c) => (
            <button key={c} type="button" className={`${classes.sw} ${accent.toLowerCase() === c.toLowerCase() ? classes.swOn : ""}`} style={{ background: c === "#A044FF" ? "linear-gradient(135deg,#A044FF,#22D3EE)" : c }} onClick={() => setAccent(c)} aria-label={c} />
          ))}
          <button type="button" className={`${classes.add} ${!ACCENTS.some((c) => c.toLowerCase() === accent.toLowerCase()) ? classes.swOn : ""}`} onClick={() => colorRef.current?.click()} aria-label={i18n.t("aeros.header.customColor")}><AddIcon /></button>
          <input ref={colorRef} type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ position: "absolute", width: 0, height: 0, opacity: 0 }} aria-hidden="true" tabIndex={-1} />
        </div>
      </div>
    </>
  );
}

export default function AppearancePopover() {
  const h = useHeaderStyles();
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip title={i18n.t("aeros.header.theme")}>
        <IconButton className={`${h.iconBtn} ${anchor ? h.iconBtnOn : ""}`} onClick={(e) => setAnchor(e.currentTarget)} aria-label={i18n.t("aeros.header.theme")}><Brightness4Icon /></IconButton>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} classes={{ paper: h.paper }} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <div className={h.pad14} style={{ width: 300 }}>
          <AppearancePanel />
        </div>
      </Popover>
    </>
  );
}
